import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { getSocket } from '../utils/socket';
import { TEAM_COLORS } from '../utils/colors';
import type { Group } from '../types';

export default function AdminGroups() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [adjustDeltas, setAdjustDeltas] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.get<Group[]>('/api/groups');
      setGroups(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    const socket = getSocket();
    const handleGroupsUpdated = ({ groups: updated }: { groups: Group[] }) => {
      setGroups(updated);
    };

    socket.on('groups:updated', handleGroupsUpdated);
    return () => { socket.off('groups:updated', handleGroupsUpdated); };
  }, []);

  const startEdit = (g: Group) => {
    setEditingId(g.id);
    setEditName(g.name);
    setError('');
  };

  const saveRename = async (id: number) => {
    if (!editName.trim()) return;
    try {
      const updated = await api.put<Group>(`/api/groups/${id}`, { name: editName.trim() });
      setGroups((prev) => prev.map((g) => g.id === id ? updated : g));
      setEditingId(null);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/groups/${id}`);
      setGroups((prev) => prev.filter((g) => g.id !== id));
      setConfirmDelete(null);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDeleteAll = async () => {
    try {
      await api.delete('/api/groups');
      setGroups([]);
      setConfirmDeleteAll(false);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const resetScore = async (id: number) => {
    try {
      const updated = await api.post<Group>(`/api/groups/${id}/reset-score`);
      setGroups((prev) => prev.map((g) => g.id === id ? updated : g));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const applyAdjust = async (id: number) => {
    const raw = adjustDeltas[id] || '';
    if (!raw.trim()) return;
    const delta = parseInt(raw);
    if (isNaN(delta)) return;
    try {
      const updated = await api.patch<Group>(`/api/groups/${id}/adjust-score`, { delta });
      setGroups((prev) => prev.map((g) => g.id === id ? updated : g));
      setAdjustDeltas((d) => ({ ...d, [id]: '' }));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const changeColor = async (id: number, color: string) => {
    try {
      const updated = await api.patch<Group>(`/api/groups/${id}/color`, { color });
      setGroups((prev) => prev.map((g) => g.id === id ? updated : g));
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className="page-loading">Chargement...</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Gestion des équipes</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <p className="text-muted">{groups.length} équipe{groups.length !== 1 ? 's' : ''}</p>
          {groups.length > 0 && (
            confirmDeleteAll ? (
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="text-danger">Supprimer toutes les équipes ?</span>
                <button className="btn btn-sm btn-danger" onClick={handleDeleteAll}>Oui, tout supprimer</button>
                <button className="btn btn-sm btn-secondary" onClick={() => setConfirmDeleteAll(false)}>Annuler</button>
              </span>
            ) : (
              <button className="btn btn-sm btn-danger" onClick={() => setConfirmDeleteAll(true)}>
                🗑 Supprimer toutes les équipes
              </button>
            )
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {groups.length === 0 ? (
        <div className="empty-state">
          <p>Aucune équipe enregistrée.<br />Les équipes apparaissent ici dès qu'un joueur rejoint.</p>
        </div>
      ) : (
        <div className="groups-admin-list">
          {groups.map((g) => (
            <div key={g.id} className="group-admin-card">
              <div className="group-admin-header">
                <span className="group-color-indicator" style={{ background: g.color }} />
                {editingId === g.id ? (
                  <div className="edit-name-row">
                    <input
                      className="edit-name-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRename(g.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      autoFocus
                    />
                    <button className="btn btn-sm btn-success" onClick={() => saveRename(g.id)}>✓</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditingId(null)}>✗</button>
                  </div>
                ) : (
                  <span className="group-name">{g.name}</span>
                )}
                <div className="group-status">
                  <span className={`status-indicator ${g.connected ? 'online' : 'offline'}`}>
                    {g.connected ? 'en ligne' : 'hors ligne'}
                  </span>
                </div>
              </div>

              <div className="group-admin-stats">
                <span className="stat"><strong>{g.totalPoints}</strong> pts</span>
              </div>

              <div className="score-adjust-row">
                <span className="label-sm">Ajuster le score :</span>
                <input
                  type="number"
                  className="score-adjust-input"
                  placeholder="+10 ou -5"
                  value={adjustDeltas[g.id] || ''}
                  onChange={(e) => setAdjustDeltas((d) => ({ ...d, [g.id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyAdjust(g.id); }}
                />
                <button className="btn btn-sm btn-secondary" onClick={() => applyAdjust(g.id)}>Appliquer</button>
              </div>

              <div className="group-admin-colors">
                <span className="label-sm">Couleur :</span>
                {TEAM_COLORS.map((c) => (
                  <button
                    key={c}
                    className={`color-dot ${g.color === c ? 'color-dot-active' : ''}`}
                    style={{ background: c }}
                    onClick={() => changeColor(g.id, c)}
                    title={c}
                  />
                ))}
              </div>

              <div className="group-admin-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => startEdit(g)}>✏ Renommer</button>
                <button className="btn btn-sm btn-warning" onClick={() => resetScore(g.id)}>↺ Reset score</button>
                {confirmDelete === g.id ? (
                  <>
                    <span className="text-danger">Confirmer ?</span>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(g.id)}>Oui, supprimer</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => setConfirmDelete(null)}>Non</button>
                  </>
                ) : (
                  <button className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(g.id)}>🗑 Supprimer</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
